import React, { useState } from 'react';
import { X, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useLeads } from '../../context/LeadsContext';

/**
 * AddClientModal — single-customer creation.
 *
 * Writes one lead doc via the existing LeadsContext.addLead pipeline.
 * Stage defaults to 'Approved' because this surface lives on the Clients
 * page (active customer book) — not on the Pipeline (prospect funnel).
 * No recurring activation here; for recurring program assignment use
 * the bulk Migrate Customers flow.
 */
const AddClientModal = ({ isOpen, onClose }) => {
  const { addLead } = useLeads();

  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [phone,     setPhone]     = useState('');
  const [email,     setEmail]     = useState('');
  const [address,   setAddress]   = useState('');
  const [city,      setCity]      = useState('');
  const [state,     setState]     = useState('');
  const [zip,       setZip]       = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const reset = () => {
    setFirstName(''); setLastName(''); setPhone(''); setEmail('');
    setAddress(''); setCity(''); setState(''); setZip('');
    setErr(''); setSaving(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const handleSave = async () => {
    setErr('');
    const trimmedFirst = firstName.trim();
    const trimmedLast  = lastName.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedFirst && !trimmedLast) {
      setErr('Please enter a first or last name.');
      return;
    }
    if (!trimmedPhone && !email.trim()) {
      setErr('Please enter a phone number or email.');
      return;
    }

    setSaving(true);
    try {
      const name = `${trimmedFirst} ${trimmedLast}`.trim();
      const addressObj = {
        line1: address.trim() || null,
        city:  city.trim()    || null,
        state: state.trim()   || null,
        zip:   zip.trim()     || null,
      };
      const display = [addressObj.line1, addressObj.city, addressObj.state, addressObj.zip]
        .filter(Boolean)
        .join(', ');

      await addLead({
        name,
        firstName: trimmedFirst || null,
        lastName:  trimmedLast  || null,
        phone:     trimmedPhone || null,
        email:     email.trim().toLowerCase() || null,
        address:   addressObj,
        addressDisplay: display,
        // Clients page = existing customer book. Default to Approved so the
        // client immediately shows in Forecast/Calendar without forcing the
        // operator to bounce through the pipeline.
        stage: 'Approved',
        source: 'manual_add_client',
      });

      toast.success(`${name || 'Client'} added`);
      reset();
      onClose();
    } catch (e) {
      console.error('[AddClientModal] error:', e);
      setErr(e?.message || 'Failed to add client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm" onClick={handleClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="add-client-modal"
        >
          <div className="flex items-center justify-between p-5 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" style={{ color: 'var(--cb-orange)' }} />
              <h2 className="text-lg font-bold" style={{ color: 'var(--cb-navy)' }}>
                Add Client
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              disabled={saving}
              data-testid="add-client-close-btn"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          <div className="p-5 space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ac-first" className="text-xs">First Name</Label>
                <Input
                  id="ac-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-first-name"
                />
              </div>
              <div>
                <Label htmlFor="ac-last" className="text-xs">Last Name</Label>
                <Input
                  id="ac-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-last-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ac-phone" className="text-xs">Phone</Label>
                <Input
                  id="ac-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-phone"
                />
              </div>
              <div>
                <Label htmlFor="ac-email" className="text-xs">Email</Label>
                <Input
                  id="ac-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="ac-addr" className="text-xs">Street Address</Label>
              <Input
                id="ac-addr"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={saving}
                data-testid="add-client-address"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="ac-city" className="text-xs">City</Label>
                <Input
                  id="ac-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-city"
                />
              </div>
              <div>
                <Label htmlFor="ac-state" className="text-xs">State</Label>
                <Input
                  id="ac-state"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-state"
                />
              </div>
              <div>
                <Label htmlFor="ac-zip" className="text-xs">ZIP</Label>
                <Input
                  id="ac-zip"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  disabled={saving}
                  data-testid="add-client-zip"
                />
              </div>
            </div>

            {err && (
              <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1.5">
                {err}
              </p>
            )}

            <p className="text-[11px] text-gray-500 leading-relaxed pt-1">
              To assign this client to a recurring Service Program, use{' '}
              <span className="font-medium">Migrate Customers</span> instead, or open the client
              after creation and activate a service plan from their profile.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={saving}
              data-testid="add-client-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="add-client-save-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
              ) : (
                'Add Client'
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddClientModal;
